// Shared navigation logic for medication-criteria items.
//
// Both the prescription detail page (PrescriptionCriteriaCard) and the
// dashboard care-action card (CareActionItem, when there's exactly one pending
// medication-criteria) build hrefs from API-supplied IDs (`required_id`).
// Centralizing this keeps the URL shape and the safety allowlist in one place
// so the dashboard short-circuit can't drift away from the detail page.
//
// `required_id` ships from the API as either a numeric DB id (e.g. "12345")
// or a UUID (e.g. "a2246a0e-b847-4b06-9426-fd2c383111a0"). We refuse anything
// outside that shape so a compromised/poisoned payload can't slip path
// traversal ("../foo"), protocol-relative URLs ("//evil.com/..."), or
// query/fragment injection into the URL we hand to next/navigation, which
// would otherwise resolve cross-origin and full-page-redirect the patient.
const SAFE_REQUIRED_ID = /^[A-Za-z0-9_-]+$/

export type CriteriaRequiredType = 'Questionnaire' | 'Sku'

/**
 * When the API reports a booked-but-incomplete lab/visit covering this criterion
 * (`lab_order_id` / `visit_id` on `PrescriptionCriteriaItem`), the
 * caller passes the corresponding override here. The helper then deep-links to
 * the existing booking (`/lab-appointment/:id` or `/visits/:id`) instead of
 * restarting the SKU booking flow at `/s/sku/.../start` or `.../screen`.
 */
export type CriteriaExistingBooking =
    | { type: 'lab'; id: string }
    | { type: 'visit'; id: string }

export type CriteriaNavigationOptions = {
    /** Lab medication criteria: `/start` creates the lab order; `/screen` would send visits to `/options`. */
    labSku?: boolean
    /**
     * When set, the patient already has a booked-but-incomplete lab/visit for
     * this criterion. Wins over the SKU booking flow.
     */
    existingBooking?: CriteriaExistingBooking | null
}

const RETURN_TO_PREFIXES = [
    '/account/medication-check-in',
    '/account',
    '/prescriptions',
    '/care-plan',
]

/** Validate a `return_to` query param against an allowlist of internal paths. */
export function validateReturnTo(value: string | null | undefined): string | null {
    if (!value) return null
    if (!value.startsWith('/') || value.startsWith('//')) return null
    if (value.includes('..')) return null

    return RETURN_TO_PREFIXES.some(p => value === p || value.startsWith(p + '/')) ? value : null
}

/** Back target for questionnaire landing pages: honor `return_to` before care-plan fallback. */
export function resolveQuestionnaireBackUrl(searchParams: {
    return_to?: string | null
    care_plan_id?: string | null
}): string | undefined {
    const returnTo = validateReturnTo(searchParams.return_to)
    if (returnTo) return returnTo
    if (searchParams.care_plan_id) return `/care-plan/${searchParams.care_plan_id}`
    return undefined
}

export type QuestionnaireFlowSearchParams = {
    return_to?: string | null
    care_plan_id?: string | null
    next_step_id?: string | null
}

/** Preserve care-journey query params when linking between questionnaire steps. */
export function buildQuestionnaireFlowSearchQuery(searchParams: QuestionnaireFlowSearchParams): string {
    const query = new URLSearchParams()
    if (searchParams.care_plan_id) query.set('care_plan_id', searchParams.care_plan_id)
    if (searchParams.next_step_id) query.set('next_step_id', searchParams.next_step_id)
    const returnTo = validateReturnTo(searchParams.return_to)
    if (returnTo) query.set('return_to', returnTo)
    const queryString = query.toString()
    return queryString ? `?${queryString}` : ''
}

/** Back target for an in-progress question: previous question, or the landing page for question 1. */
export function resolveQuestionnaireQuestionBackUrl(
    questionnaireId: string,
    questionNumber: number,
    searchParams: QuestionnaireFlowSearchParams,
): string {
    const querySuffix = buildQuestionnaireFlowSearchQuery(searchParams)
    if (questionNumber > 1) {
        return `/questionnaire/${questionnaireId}/${questionNumber - 1}${querySuffix}`
    }
    return `/questionnaire/${questionnaireId}${querySuffix}`
}

// Resolving against a fixed origin is what discards the host of an absolute or
// protocol-relative value; the origin itself never reaches a link.
const QUESTIONNAIRE_URL_RESOLUTION_ORIGIN = 'https://questionnaire.invalid'

/**
 * Reduces an API-supplied questionnaire URL to a path on the current origin, or
 * null when it cannot be one. API URLs are absolute (built server-side), and
 * dropping the origin also keeps the link on the page's own scheme and host —
 * local dev serves http://localhost while the API may emit https://localhost,
 * which triggers ERR_SSL_PROTOCOL_ERROR.
 *
 * The same reduction is the allowlist that keeps a poisoned payload out of the
 * `href`: a foreign host survives only as its path, and a scheme that carries
 * code or data rather than a location (`javascript:`, `data:`) is refused, so a
 * caller that renders null as no link cannot navigate off-site or execute a URL.
 */
export function sameOriginQuestionnairePath(questionnaireUrl: string): string | null {
    const trimmed = questionnaireUrl?.trim()
    if (!trimmed) return null

    try {
        const parsed = new URL(trimmed, QUESTIONNAIRE_URL_RESOLUTION_ORIGIN)
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
        const path = `${parsed.pathname}${parsed.search}${parsed.hash}`
        if (path.startsWith('//')) return null
        return path
    } catch {
        return null
    }
}

function appendReturnTo(path: string, returnToPath: string | null | undefined): string {
    const validated = validateReturnTo(returnToPath ?? null)
    if (!validated) return path
    const sep = path.includes('?') ? '&' : '?'
    return `${path}${sep}return_to=${encodeURIComponent(validated)}`
}

/**
 * Append allowed `return_to` to a questionnaire entry URL the API supplied.
 * Null when the URL cannot address this origin; callers render no link for it.
 */
export function appendReturnToToQuestionnaireUrl(
    questionnaireUrl: string,
    returnToPath: string | null | undefined,
): string | null {
    const path = sameOriginQuestionnairePath(questionnaireUrl)
    if (!path) return null

    return appendReturnTo(path, returnToPath)
}

/**
 * Append allowed `return_to` to a questionnaire path this app built, e.g. from
 * `getCriteriaNavigationPath`. Use `appendReturnToToQuestionnaireUrl` for a URL
 * that came from the API, which has to clear the same-origin allowlist first.
 */
export function appendReturnToToQuestionnairePath(
    questionnairePath: string,
    returnToPath: string | null | undefined,
): string {
    return appendReturnTo(questionnairePath, returnToPath)
}

/**
 * Predicate shared between this module and callers (e.g. PrescriptionCriteriaCard)
 * so they agree on when an `existingBooking` will actually short-circuit the SKU
 * flow. Exposing this prevents the caller from skipping `?return_to` in cases
 * where the helper rejected the booking id and fell through to the legacy path.
 */
export function isExistingBookingNavigable(
    booking: CriteriaExistingBooking | null | undefined,
): booking is CriteriaExistingBooking {
    return booking != null && SAFE_REQUIRED_ID.test(booking.id)
}

export function getCriteriaNavigationPath(
    requiredType: CriteriaRequiredType | string | undefined | null,
    requiredId: string | undefined | null,
    options?: CriteriaNavigationOptions,
): string | null {
    // Existing-booking override wins when a covering lab/visit is present.
    // Reuse the same SAFE_REQUIRED_ID allowlist on the booking id so a poisoned
    // payload can't slip path traversal or scheme injection through this branch.
    const booking = options?.existingBooking
    if (isExistingBookingNavigable(booking)) {
        const id = encodeURIComponent(booking.id)
        return booking.type === 'lab' ? `/lab-appointment/${id}` : `/visits/${id}`
    }

    if (!requiredId || !SAFE_REQUIRED_ID.test(requiredId)) return null

    const id = encodeURIComponent(requiredId)
    if (requiredType === 'Questionnaire') return `/questionnaire/${id}`

    if (requiredType === 'Sku') {
        if (options?.labSku) {
            // `/start/page.tsx` canonicalizes the slug like `/screen` then creates the lab order.
            return `/s/sku/${id}/start`
        }
        // Visit SKU: "Get started" via /screen — self-corrected to real slug, then
        // /location-confirmation or /options (see SkuDetailCta + screen/page.tsx).
        return `/s/sku/${id}/screen`
    }
    return null
}
